import flow from 'lodash/flow';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import toPairs from 'lodash/toPairs';
import update from 'lodash/update';

import { billableFields } from '../../constants';
import { calculateBillableBudget } from '../../utils/billable-field-helpers';
import { removeUnchangedValues } from '../../utils/remove-unchanged-values';
import { isArtificialId } from './get-id';

const PRODUCT_FLAGS = [
  { flag: 'overwriteGender', allowProp: 'genderMenShare' },
  { flag: 'overwriteGender', allowProp: 'genderWomenShare' },
  { flag: 'overwriteGender', allowProp: 'genderKidsShare' },
  { flag: 'overwriteCategories', allowProp: 'categories' },
  { flag: 'hasFreeService', allowProp: 'freeService' },
  { flag: 'serviceDescription', allowProp: 'serviceDesc' },
  { flag: 'overwriteTimeRange', allowProp: 'startDate' },
  { flag: 'overwriteTimeRange', allowProp: 'endDate' },
];

function omitReadonlyProperties(quoteLine) {
  return omit(quoteLine, [
    'number',
    'lineNumber',
    'quoteId',
    'created',
    'lastModified',
    'createdBy',
    'lastModifiedBy',
    'genders',
    'filteredOut',
    'netClientBudget',
  ]);
}
function omitProductSpecificProperties(product) {
  return quoteLine =>
    omit(
      quoteLine,
      PRODUCT_FLAGS.filter(({ flag }) => !product[flag]).map(
        ({ allowProp }) => allowProp,
      ),
    );
}
function omitArtificialId(quoteLine) {
  if (isArtificialId(quoteLine.id)) {
    return omit(quoteLine, 'id');
  }
  return quoteLine;
}
function transformCountrySplit(quoteLine) {
  update(quoteLine, 'countrySplit', countrySplit =>
    toPairs(countrySplit)
      .map(([countryCode, share]) => ({
        countryCode,
        share,
      }))
      .filter(({ share }) => share > 0),
  );
  return quoteLine;
}

export function transformQuoteLineForPatch(completeQuoteLine, product) {
  return flow(
    omitReadonlyProperties,
    omitArtificialId,
    transformCountrySplit,
    omitProductSpecificProperties(product),
  )(completeQuoteLine);
}

export function removeQuoteLineFieldsForClone(quoteLine) {
  const newQuoteLine = omit(quoteLine, [
    'id',
    'number',
    'lineNumber',
    'quoteId',
    'created',
    'lastModified',
    'createdBy',
    'lastModifiedBy',
    'countrySplit',
    'genders',
  ]);
  if (!isEmpty(quoteLine.countrySplit)) {
    newQuoteLine.countrySplit = quoteLine.countrySplit.map(split =>
      omit(split, [
        'id',
        'quoteLineId',
        'created',
        'lastModified',
        'createdBy',
        'lastModifiedBy',
      ]),
    );
  }
  return newQuoteLine;
}

export function removeQuoteFieldsForClone(quote) {
  return omit(quote, [
    'id',
    'created',
    'lastModified',
    'createdBy',
    'lastModifiedBy',
    'feeDiscount',
    'freeServiceValue',
    'grossOfferValue',
    'number',
    'netBudget',
    'campaignId',
    'quoteLines',
    'genders',
    'categories',
  ]);
}

export function transformQuoteToInitialState(quote, productsById) {
  return {
    ...quote.quote,
    quoteLines: Object.keys(quote?.quoteLinesById).map(id => {
      const line = quote?.quoteLinesById[id];
      const product = productsById[line.productId];
      return transformQuoteLineForPatch(line, product);
    }),
  };
}

export default function transformForPatch({
  quote,
  quoteLinesById,
  productsById,
  opportunity,
  initialQuote = {},
}) {
  const oldQuote = transformQuoteToInitialState(initialQuote, productsById);

  const quoteCopy = omit(quote, [
    'id',
    'number',
    'brandIds',
    'opportunityId',
    'created',
    'lastModified',
    'createdBy',
    'lastModifiedBy',
    'netBudget',
    'feeDiscount',
    'coInvest',
    'freeServiceValue',
    'grossOfferValue',
    'ui',
    'netClientBudget',
    'netClientBudgetExternal',
    'wkzBudget',
  ]);

  quoteCopy.quoteLines = Object.keys(quoteLinesById).map(id => {
    const line = quoteLinesById[id];
    const product = productsById[line.productId];
    return transformQuoteLineForPatch(line, product);
  });

  // The billable fields are not updated in store and hence doing the below to get the updated billable values.
  const billableBudgetFields = calculateBillableBudget({
    quote,
    quoteLinesById,
    productsById,
    opportunity,
  });
  billableFields.forEach(field => {
    quoteCopy[field] = billableBudgetFields[field];
  });

  const renderQuote = removeUnchangedValues(quoteCopy, oldQuote);

  return renderQuote;
}
